import "./../JSONTools/JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import { Row, Col } from "react-bootstrap";
import { useState, useRef, useCallback } from "react";
import { DocxtoPDFAPI } from "../../Services/APIService";

const PAGE_LAYOUT_OPTIONS = [
    { value: "PRESERVE_ORIGINAL_LAYOUT:", label: "Preserve Original Layout" },
    { value: "FIT_TO_PAGE_WIDTH", label: "Fit to Page Width" },
    { value: "PRESERVE_MARGINS", label: "Preserve Margins" },
];

const QUALITY_OPTIONS = [
    { value: "STANDARD", label: "Standard" },
    { value: "HIGH_QUALITY", label: "High Quality" },
    { value: "PRINT_READY", label: "Print ready" },
];

const PROGRESS_STEPS = ["Uploading", "Processing", "Generating PDF", "Completed"];

function formatBytes(bytes) {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
}

function StepBadge({ number }) {
    return <span className="dpc-badge">{number}</span>;
}

function ChevronIcon({ open }) {
    return (
        <svg
            className={`dpc-chevron ${open ? "dpc-chevron-open" : ""}`}
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
        >
            <path d="M5 7.5L10 12.5L15 7.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function RadioRow({ name, value, checked, onChange, label }) {
    return (
        <label className="dpc-radio-row">
            <input type="radio" name={name} value={value} checked={checked} onChange={onChange} />
            {label}
        </label>
    );
}

function Section({ title, open, onToggle, children }) {
    return (
        <div className="dpc-section">
            <button type="button" onClick={onToggle} className="dpc-section-header">
                <span>{title}</span>
                <ChevronIcon open={open} />
            </button>
            {open && <div className="dpc-section-body">{children}</div>}
        </div>
    );
}


function DocxtoPDFConverter() {
    const toolFav = { toolName: 'DOCX to PDF', Iconpath: "/FileCodeRaw.svg" };
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [pageLayout, setPageLayout] = useState("preserve-layout");
    const [outputQuality, setOutputQuality] = useState("standard");
    const [openSection, setOpenSection] = useState("page");
    const [stage, setStage] = useState("idle"); 
    const [completedSteps, setCompletedSteps] = useState([]);
    const [progressPct, setProgressPct] = useState(0);
    const [result, setResult] = useState(null); 
    const [errorMsg, setErrorMsg] = useState("");
    const [isCopied, setIsCopied] = useState(false);
    const inputRef = useRef(null);
    const timersRef = useRef([]);

    

    

    const clearTimers = () => {
        timersRef.current.forEach((t) => clearTimeout(t));
        timersRef.current = [];
    };

    const handleFiles = useCallback((fileList) => {
        const picked = fileList?.[0];
        if (!picked) return;
        const okType = /\.docx?$/i.test(picked.name);
        if (!okType) {
            setErrorMsg("Please upload a .docx or .doc file.");
            return;
        }
        setErrorMsg("");
        setFile(picked);
        setStage("idle");
        setResult(null);
    }, []);

    const onDrop = useCallback(
        (e) => {
            e.preventDefault();
            setDragActive(false);
            handleFiles(e.dataTransfer.files);
        },
        [handleFiles]
    );

    const removeFile = () => {
        setFile(null);
        setStage("idle");
        setResult(null);
    };

    const runFakeProgress = () => {
        clearTimers();
        setCompletedSteps([]);
        setProgressPct(0);
        const timings = [300, 900, 1500]; // ms marks for the first 3 steps
        timings.forEach((t, i) => {
            const id = setTimeout(() => {
                setCompletedSteps((prev) => (prev.includes(i) ? prev : [...prev, i]));
                setProgressPct(Math.round(((i + 1) / PROGRESS_STEPS.length) * 100));
            }, t);
            timersRef.current.push(id);
        });
    };

    const handleConvert = async () => {
        if (!file) return;
        setStage("converting");
        setErrorMsg("");
        runFakeProgress();

        try {
            const formData = new FormData();
            formData.append("files", file);
            formData.append("pageLayout", pageLayout);
            formData.append("outputQuality", outputQuality);

            const response = await DocxtoPDFAPI(formData);
            const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(blob);

            setCompletedSteps([0, 1, 2, 3]);
            setProgressPct(100);
            setResult({
                blobUrl,
                size: blob.size,
                filename: file.name.replace(/\.docx?$/i, ".pdf"),
            });
            setStage("done");
        } catch (err) {
            clearTimers();
            setErrorMsg(err.message || "Something went wrong during conversion.");
            setStage("error");
        }
    };

    const downloadResult = () => {
        if (!result) return;
        const a = document.createElement("a");
        a.href = result.blobUrl;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const copyLink = async () => {
        if (!result) return;
        try {
            await navigator.clipboard.writeText(result.blobUrl);
        } catch {
            /* clipboard not available in this context */
        }
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <>
            <Col>
                <ToolHeader title="DOCX to PDF" description="Convert DOCX files to PDF format" toolFav={toolFav} />

                <Row >
                    <div className="dpc-shell">
                        {/* Step 1 - Upload */}
                        <div className="dpc-card">
                            <div className="dpc-card-header">
                                <StepBadge number={1} />
                                <span className="dpc-card-title">Upload File</span>
                            </div>
                            <div className="dpc-card-body">
                                <div
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        setDragActive(true);
                                    }}
                                    onDragLeave={() => setDragActive(false)}
                                    onDrop={onDrop}
                                    className={`dpc-dropzone ${dragActive ? "dpc-dropzone-active" : ""}`}
                                >
                                    <svg
                                        className="dpc-upload-icon"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M12 16V4M12 4L7 9M12 4L17 9" />
                                        <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                                    </svg>
                                    <p className="dpc-dropzone-title">Drop your DOCX file here</p>
                                    <p className="dpc-dropzone-subtitle">Supported: .docx, .doc &middot; Max 25 MB</p>
                                    <button type="button" onClick={() => inputRef.current?.click()} className="dpc-btn dpc-btn-primary">
                                        Select file
                                    </button>
                                    <input
                                        ref={inputRef}
                                        type="file"
                                        accept=".docx,.doc"
                                        className="dpc-hidden-input"
                                        onChange={(e) => handleFiles(e.target.files)}
                                    />
                                </div>

                                {file && (
                                    <div className="dpc-file-chip">
                                        <svg className="dpc-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                            <path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
                                            <path d="M14 3v4h4" />
                                        </svg>
                                        <div className="dpc-file-meta">
                                            <p className="dpc-file-name">{file.name}</p>
                                            <p className="dpc-file-sub">{formatBytes(file.size)} &middot; Ready</p>
                                        </div>
                                        <button type="button" onClick={removeFile} className="dpc-remove-btn">
                                            &times;
                                        </button>
                                    </div>
                                )}
                                {errorMsg && stage !== "converting" && stage !== "error" && (
                                    <p className="dpc-error-text">{errorMsg}</p>
                                )}
                            </div>
                        </div>

                        {/* Step 2 - Settings */}
                        <div className="dpc-card">
                            <div className="dpc-card-header">
                                <StepBadge number={2} />
                                <span className="dpc-card-title">PDF Settings</span>
                            </div>
                            <Section
                                title="Page Settings"
                                open={openSection === "page"}
                                onToggle={() => setOpenSection(openSection === "page" ? null : "page")}
                            >
                                {PAGE_LAYOUT_OPTIONS.map((opt) => (
                                    <RadioRow
                                        key={opt.value}
                                        name="pageLayout"
                                        value={opt.value}
                                        checked={pageLayout === opt.value}
                                        onChange={() => setPageLayout(opt.value)}
                                        label={opt.label}
                                    />
                                ))}
                            </Section>
                            <Section
                                title="Output Quality"
                                open={openSection === "quality"}
                                onToggle={() => setOpenSection(openSection === "quality" ? null : "quality")}
                            >
                                {QUALITY_OPTIONS.map((opt) => (
                                    <RadioRow
                                        key={opt.value}
                                        name="outputQuality"
                                        value={opt.value}
                                        checked={outputQuality === opt.value}
                                        onChange={() => setOutputQuality(opt.value)}
                                        label={opt.label}
                                    />
                                ))}
                            </Section>
                        </div>

                        {/* Step 3 - Convert */}
                        <div className="dpc-card dpc-card-padded">
                            <div className="dpc-card-header dpc-mb">
                                <StepBadge number={3} />
                                <span className="dpc-card-title">Convert</span>
                            </div>
                            <button
                                type="button"
                                disabled={!file || stage === "converting"}
                                onClick={handleConvert}
                                className="dpc-btn dpc-btn-primary dpc-btn-full"
                            >
                                {stage === "converting" ? "Converting..." : "Convert to PDF"}
                            </button>

                            {stage === "converting" && (
                                <div className="dpc-progress">
                                    <div className="dpc-progress-steps">
                                        {PROGRESS_STEPS.map((label, i) => (
                                            <span key={label} className={`dpc-progress-step ${completedSteps.includes(i) ? "dpc-step-done" : ""}`}>
                                                {completedSteps.includes(i) ? "✓" : "○"} {label}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="dpc-progress-bar">
                                        <div className="dpc-progress-fill" style={{ width: `${progressPct}%` }} />
                                    </div>
                                </div>
                            )}

                            {stage === "error" && <p className="dpc-error-text dpc-mt">{errorMsg}</p>}
                        </div>

                        {/* Step 4 - Result */}
                        {stage === "done" && result && (
                            <div className="dpc-card dpc-card-padded">
                                <div className="dpc-card-header dpc-mb">
                                    <StepBadge number={4} />
                                    <span className="dpc-card-title">Result</span>
                                </div>
                                <div className="dpc-success-banner">PDF generated successfully</div>
                                <div className="dpc-result-row">
                                    <div className="dpc-result-meta">
                                        <p className="dpc-file-name">{result.filename}</p>
                                        <p className="dpc-file-sub dpc-mb-sm">{formatBytes(result.size)}</p>
                                        <div className="dpc-result-actions">
                                            <button type="button" onClick={downloadResult} className="dpc-btn dpc-btn-primary dpc-btn-sm">
                                                Download PDF
                                            </button>
                                            <button type="button" onClick={copyLink} className="dpc-btn dpc-btn-outline dpc-btn-sm">
                                                {isCopied ? "Copied !" : "Copy link"}
                                            </button>
                                        </div>
                                        <div className="dpc-size-compare">
                                            <div>
                                                <p className="dpc-size-label">Original</p>
                                                <p className="dpc-size-value">{formatBytes(file?.size)}</p>
                                            </div>
                                            <div>
                                                <p className="dpc-size-label">Converted</p>
                                                <p className="dpc-size-value">{formatBytes(result.size)}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="dpc-preview">
                                        <iframe title="PDF preview" src={result.blobUrl} className="dpc-preview-frame" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Row>
            </Col >
        </>
    )
}

export default DocxtoPDFConverter;
